import { importSPKI, jwtVerify, type JWTPayload } from 'jose';

export interface SessionClaims {
  tenantId: string;
  role: string;
  contextId?: string;
  lmsUserId?: string;
  email?: string;
  name?: string;
  // NameDrop org slug. Present iff the tenant has nickname configured.
  // Used by the SPA to gate the pronunciation/profile UI.
  nickname?: string;
  // Which view to render. Defaults to 'default' when absent.
  view?: string;
}

export class SessionVerificationError extends Error {
  public readonly code:
    | 'missing_public_key'
    | 'invalid_token'
    | 'invalid_claims';

  constructor(
    code:
      | 'missing_public_key'
      | 'invalid_token'
      | 'invalid_claims',
    message: string,
  ) {
    super(message);
    this.name = 'SessionVerificationError';
    this.code = code;
  }
}

const ALG = 'ES256';

function readString(payload: JWTPayload, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' ? value : undefined;
}

function toSessionClaims(payload: JWTPayload): SessionClaims {
  const tenantId = readString(payload, 'tenantId');
  const role = readString(payload, 'role');
  if (!tenantId || !role) {
    throw new SessionVerificationError(
      'invalid_claims',
      'Session token is missing required claims (tenantId, role).',
    );
  }
  const claims: SessionClaims = { tenantId, role };
  const contextId = readString(payload, 'contextId');
  if (contextId !== undefined) claims.contextId = contextId;
  const lmsUserId = readString(payload, 'lmsUserId');
  if (lmsUserId !== undefined) claims.lmsUserId = lmsUserId;
  const email = readString(payload, 'email');
  if (email !== undefined) claims.email = email;
  const name = readString(payload, 'name');
  if (name !== undefined) claims.name = name;
  const nickname = readString(payload, 'nickname');
  if (nickname !== undefined) claims.nickname = nickname;
  const view = readString(payload, 'view');
  if (view !== undefined) claims.view = view;
  return claims;
}

export async function verifySessionJwt(token: string): Promise<SessionClaims> {
  const pem = import.meta.env.VITE_SESSION_PUBLIC_KEY_PEM;
  if (typeof pem !== 'string' || pem.trim().length === 0) {
    throw new SessionVerificationError(
      'missing_public_key',
      'VITE_SESSION_PUBLIC_KEY_PEM is not configured for this build.',
    );
  }

  let key: CryptoKey;
  try {
    key = await importSPKI(pem, ALG);
  } catch (cause) {
    throw new SessionVerificationError(
      'missing_public_key',
      `Failed to import session public key: ${
        cause instanceof Error ? cause.message : String(cause)
      }`,
    );
  }

  try {
    const { payload } = await jwtVerify(token, key, { algorithms: [ALG] });
    return toSessionClaims(payload);
  } catch (cause) {
    if (cause instanceof SessionVerificationError) throw cause;
    throw new SessionVerificationError(
      'invalid_token',
      `Session token failed verification: ${
        cause instanceof Error ? cause.message : String(cause)
      }`,
    );
  }
}
